import { useCallback, useEffect, useRef, useState } from 'react';
import messageService from '../../../services/messageService';
import { onPlanSuggested } from '../../../services/messagesRealtime';

/**
 * The plan-detection state for one open thread: the viewer's draft (loaded on open, pushed over
 * the hub), × and UNDO, FIND A PLAN, and the per-chat switch. Private by construction — the
 * server only ever answers for the viewer.
 */
export default function usePlanDetection({ threadId, isSecure = false, locked = false, onToast }) {
  const [draft, setDraft] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [globalEnabled, setGlobalEnabled] = useState(false);
  // Bumped when a NEW read lands (a push or FIND), so the bar replays its arrival.
  const [arrival, setArrival] = useState(0);
  const threadRef = useRef(threadId);
  threadRef.current = threadId;

  useEffect(() => {
    setDraft(null); setDismissed(false); setArrival(0);
    if (!threadId || isSecure || locked) return undefined;
    let cancelled = false;
    (async () => {
      const r = await messageService.getPlan?.(threadId);
      if (cancelled || !r?.success) return;
      const d = r.data || null;
      setDraft(d);
      setDismissed(!!d && d.status === 'dismissed');
      setArrival((a) => a + 1);
    })();
    return () => { cancelled = true; };
  }, [threadId, isSecure, locked]);

  // The switches: one read of the settings per mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await messageService.getPlanDetection?.();
      if (cancelled || !r?.success || !r.data) return;
      setGlobalEnabled(!!r.data.enabled);
      const row = (r.data.threads || []).find((t) => t.threadId === threadId);
      setChatEnabled(row ? !!row.enabled : true);
    })();
    return () => { cancelled = true; };
  }, [threadId]);

  useEffect(() => (onPlanSuggested || (() => () => {}))((plan) => {
    if (!plan || plan.threadId !== threadRef.current) return;
    setDraft(plan);
    setDismissed(plan.status === 'dismissed');
    setArrival((a) => a + 1);
  }), []);

  const dismiss = useCallback(async () => {
    if (!threadId) return;
    setDismissed(true);
    const r = await messageService.dismissPlan(threadId);
    if (!r?.success) { setDismissed(false); onToast?.('COULDN’T DO THAT'); return; }
    if (r.data) setDraft(r.data);
  }, [threadId, onToast]);

  const undo = useCallback(async () => {
    if (!threadId) return;
    const r = await messageService.undoDismissPlan(threadId);
    if (!r?.success) { onToast?.('COULDN’T DO THAT'); return; }
    setDismissed(false);
    if (r.data) { setDraft(r.data); setArrival((a) => a + 1); }
  }, [threadId, onToast]);

  /** FIND A PLAN. Resolves 'found' (a draft came back), 'none' (the chat holds no plan) or 'error' (the read failed). */
  const find = useCallback(async (range) => {
    if (!threadId) return 'error';
    let timeZone = null;
    try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { /* old engines */ }
    let lat = null; let lng = null;
    try {
      const loc = JSON.parse(localStorage.getItem('pr.userLocation') || 'null');
      if (loc && typeof loc.lat === 'number' && typeof loc.lng === 'number') { lat = loc.lat; lng = loc.lng; }
    } catch { /* no stored location */ }
    const r = await messageService.findPlan(threadId, { range, timeZone, lat, lng });
    if (!r?.success) return 'error';
    if (!r.data) return 'none';
    setDraft(r.data);
    setDismissed(false);
    setArrival((a) => a + 1);
    return 'found';
  }, [threadId, onToast]);

  const setChat = useCallback(async (enabled) => {
    if (!threadId) return;
    setChatEnabled(enabled);
    const r = await messageService.setThreadPlanDetection(threadId, enabled);
    if (!r?.success) { setChatEnabled(!enabled); onToast?.('COULDN’T SAVE THAT'); }
  }, [threadId, onToast]);

  /** The published card landed (or the composer told us): the suggestion is done. */
  const published = useCallback((kind, eventId) => {
    setDraft((d) => (d ? { ...d, status: 'published', publishedKind: kind, publishedEventId: eventId } : d));
  }, []);

  const suggested = !!draft && draft.status === 'suggested' && !dismissed;
  return { draft, suggested, dismissed, arrival, chatEnabled, globalEnabled, dismiss, undo, find, setChat, published, setDraft };
}
